import {
	type PartialKeys,
	type Range,
	type Virtualizer,
	type VirtualizerOptions,
	useVirtualizer,
	// biome-ignore lint/style/noRestrictedImports: encapsulation
} from "@tanstack/react-virtual";
import React, { useCallback, useLayoutEffect, useRef } from "react";

import { type BufferedRange, getBufferedIndexes } from "./_get-buffered-indexes";

type InheritedVirtualizerOptions<TScrollElement extends Element, TItemElement extends Element> = PartialKeys<
	VirtualizerOptions<TScrollElement, TItemElement>,
	"observeElementRect" | "observeElementOffset" | "scrollToFn"
>;

export interface UseVirtualOptions<TScrollElement extends Element, TItemElement extends Element>
	extends Omit<InheritedVirtualizerOptions<TScrollElement, TItemElement>, "estimateSize"> {
	estimateSize: number | ((index: number) => number);
}

/**
 * This is a copy-pasted code of the sandbox https://codesandbox.io/s/useVirtual-jwhpwn?file=/src/useVirtual.ts
 *
 * The buffering guarantees that there will be at least `overscan` and no more than `4 * overscan` extra items rendered on
 * the visible range borders. When the virtualized list is scrolled between [overscan, 4 * overscan] items virtualization remains the same,
 * so it busts performance of the virtualization approach.
 *
 * Reed more details in the discussion https://github.com/TanStack/virtual/discussions/212#discussioncomment-2682865 above.
 */
export function useVirtual<TScrollElement extends Element, TItemElement extends Element>({
	estimateSize,
	...options
}: UseVirtualOptions<TScrollElement, TItemElement>): Virtualizer<TScrollElement, TItemElement> {
	// it should keep track of the current range in order to shrink, extend or keep the same output range
	const currentRangeRef = useRef<BufferedRange>(undefined);

	const rangeExtractor = useCallback((range: Range) => getBufferedIndexes(range, currentRangeRef.current), []);

	const result = useVirtualizer({
		rangeExtractor,
		estimateSize: React.useCallback(
			(index: number) => (typeof estimateSize === "number" ? estimateSize : estimateSize(index)),
			[estimateSize],
		),
		...options,
	});

	const virtualItems = result.getVirtualItems();

	const virtualItemsStartIndex = virtualItems[0]?.index ?? 0;
	const virtualItemsEndIndex = virtualItems[virtualItems.length - 1]?.index ?? virtualItems.length - 1;

	// updates the current range when it changes
	useLayoutEffect(() => {
		currentRangeRef.current = {
			bufferStartIndex: virtualItemsStartIndex,
			bufferEndIndex: virtualItemsEndIndex,
		};
	}, [virtualItemsEndIndex, virtualItemsStartIndex]);

	return result;
}

// biome-ignore lint/style/noRestrictedImports: re-export
export type { VirtualItem, Virtualizer } from "@tanstack/react-virtual";
